import { useState } from "react";
import { Chip, Collapse, Divider } from "@mui/material";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { TC, FS, FW } from "../../tokens/text";

// ── Service catalogue ─────────────────────────────────────────────────────────
// Each entry describes one third-party integration: what it is, why LedgerHub
// uses it, what would break without it, and where to manage it.
const SERVICES = [
  // ── Email ──────────────────────────────────────────────────────────────────
  {
    category: "Email",
    name: "Zoho Mail (IMAP)",
    status: "active",
    logo: "https://www.zoho.com/mail/images/zohomail-logo.png",
    website: "https://mail.zoho.eu",
    what: "Zoho Mail is the hosted email service used for the practice mailbox (admin@ledgerhubltd.co.uk). It runs on Zoho's EU data centre.",
    why: "LedgerHub connects to Zoho Mail over IMAP every 5 minutes to pull in every email your team sends and receives. Emails are automatically matched to the right client and appear in the Email Inbox.",
    without: "No emails will be synced. The Email Inbox will show nothing new, and clients won't be linked to their correspondence automatically.",
    details: [
      { label: "Host", value: "imap.zoho.eu:993 (EU datacenter)" },
      { label: "Poll interval", value: "Every 5 minutes" },
      { label: "Folders synced", value: "INBOX, Sent, Archive + others (not Spam/Trash/Drafts)" },
      { label: "Env vars", value: "ZOHO_EMAIL, ZOHO_PASSWORD, IMAP_HOST, IMAP_PORT" },
    ],
  },
  {
    category: "Email",
    name: "Zoho Mail (SMTP)",
    status: "active",
    logo: "https://www.zoho.com/mail/images/zohomail-logo.png",
    website: "https://mail.zoho.eu",
    what: "SMTP is the outbound-sending side of Zoho Mail, used to send emails from the app (e.g. record requests, notifications).",
    why: "When the app sends a record request or reminder to a client, it routes the email through Zoho SMTP so it arrives from your real practice email address.",
    without: "Record request emails and client notifications will fail to send.",
    details: [
      { label: "Host", value: "smtp.zoho.eu:587" },
      { label: "Env vars", value: "SMTP_HOST, SMTP_PORT, ZOHO_EMAIL, ZOHO_PASSWORD" },
    ],
  },
  {
    category: "Email",
    name: "Postmark",
    status: "active",
    logo: "https://postmarkapp.com/images/postmark-logo.svg",
    website: "https://postmarkapp.com",
    what: "Postmark is a transactional email delivery service that handles inbound email parsing and reliable outbound delivery for LedgerHub.",
    why: "Record request emails and automated notifications are sent via Postmark, which tracks delivery, opens, and bounces. Postmark also provides a webhook that fires when a reply lands in the shared inbox.",
    without: "Transactional emails (record requests, reminders) may not be delivered reliably. Inbound email webhook parsing will stop working.",
    details: [
      { label: "Inbound webhook", value: "POST /api/v1/inbound-email" },
      { label: "Env vars", value: "POSTMARK_SERVER_TOKEN, POSTMARK_FROM_EMAIL" },
    ],
  },

  // ── Storage & Database ─────────────────────────────────────────────────────
  {
    category: "Storage & Database",
    name: "Firebase / Firestore",
    status: "active",
    logo: "https://firebase.google.com/images/brand-guidelines/logo-vertical.png",
    website: "https://console.firebase.google.com/project/el4-node-rest-service",
    what: "Firebase is Google's cloud platform. LedgerHub uses two of its services: Firestore (the database) and Firebase Auth (user sign-in).",
    why: [
      "Firestore is the main database — every client profile, email, job, VAT return, task, role, and notification is stored here.",
      "Firebase Auth handles user login, password reset, and role-based access (admin vs user vs visitor).",
    ],
    without: "The entire application stops working — no login, no data, nothing.",
    details: [
      { label: "Project", value: "el4-node-rest-service" },
      { label: "Database", value: "Firestore (NoSQL)" },
      { label: "Auth", value: "Firebase Authentication (email/password + custom claims)" },
      { label: "Env vars", value: "FIREBASE_PROJECT_ID, FIREBASE_SERVICE_ACCOUNT_JSON" },
    ],
  },
  {
    category: "Storage & Database",
    name: "AWS S3",
    status: "active",
    logo: "https://upload.wikimedia.org/wikipedia/commons/b/bc/Amazon-S3-Logo.svg",
    website: "https://s3.console.aws.amazon.com",
    what: "Amazon S3 (Simple Storage Service) is a cloud file-storage service. LedgerHub uses it to store email attachments and director passport images.",
    why: "When a client emails in a PDF, spreadsheet, or any attachment, the file is uploaded to S3. A short-lived (15-minute) secure link is generated whenever you click to preview or download it — the file is never exposed publicly.",
    without: "Email attachments cannot be saved or previewed. Director passport uploads will fail.",
    details: [
      { label: "Bucket", value: "ledgerhub-email-attachements-822013580049-eu-north-1-an" },
      { label: "Region", value: "eu-north-1 (Stockholm)" },
      { label: "Access", value: "Pre-signed URLs (15-minute expiry)" },
      { label: "Env vars", value: "AWS_ACCESS_KEY_ID, AWS_SECRET_ACCESS_KEY, AWS_REGION, AWS_S3_BUCKET" },
    ],
  },

  // ── AI ─────────────────────────────────────────────────────────────────────
  {
    category: "AI & Automation",
    name: "Google Gemini",
    status: "active",
    logo: "https://upload.wikimedia.org/wikipedia/commons/8/8a/Google_Gemini_logo.svg",
    website: "https://aistudio.google.com",
    what: "Google Gemini is an AI language and vision model used for AI-assisted features inside LedgerHub.",
    why: [
      "Passport / ID scanning — when you upload a director's passport, Gemini reads the document and extracts the name, date of birth, and expiry date automatically.",
      "AI chat — the in-app assistant that can answer questions about client data.",
      "Transaction categorisation — AI-suggested bookkeeping categories for bank transactions.",
      "VAT AI — helps draft VAT return narratives and flag anomalies.",
    ],
    without: "Passport scanning will return blank fields. AI chat and AI-assisted categorisation will be unavailable.",
    details: [
      { label: "Model", value: "Gemini Vision (for ID extraction), Gemini Pro (for chat)" },
      { label: "Env vars", value: "GEMINI_API_KEY" },
    ],
  },

  // ── Government ─────────────────────────────────────────────────────────────
  {
    category: "Government",
    name: "Companies House API",
    status: "active",
    logo: "https://developer.company-information.service.gov.uk/images/govuk-logo.png",
    website: "https://developer.company-information.service.gov.uk",
    what: "The Companies House API gives access to the UK's official company register. LedgerHub uses it to look up company details and file confirmation statements (CS01) and director change forms (AD01) electronically.",
    why: "When you add a client company, LedgerHub fetches its registered address, SIC codes, officers, and filing history from Companies House automatically. CS01 and AD01 forms are filed directly via the Filing API.",
    without: "Auto-fill of company details will stop working. Electronic filing of CS01/AD01 will fail — you'd need to file on the Companies House portal manually.",
    details: [
      { label: "Read API", value: "api.company-information.service.gov.uk" },
      { label: "Filing API", value: "xmlgw.companieshouse.gov.uk (SOAP)" },
      { label: "Env vars", value: "CH_API_KEY" },
    ],
  },
];

// ── Status badge colours ──────────────────────────────────────────────────────
const STATUS_COLOR = {
  active:     { bg: "rgba(76,175,80,0.15)",  text: "#4caf50", label: "Active"     },
  configured: { bg: "rgba(33,150,243,0.15)", text: "#2196f3", label: "Configured" },
  inactive:   { bg: "rgba(158,158,158,0.15)",text: "#9e9e9e", label: "Inactive"   },
};

const CATEGORIES = [...new Set(SERVICES.map((s) => s.category))];

// ── Card component ────────────────────────────────────────────────────────────
function ServiceCard({ service }) {
  const [open, setOpen] = useState(false);
  const sc = STATUS_COLOR[service.status] || STATUS_COLOR.configured;
  const why = Array.isArray(service.why) ? service.why : [service.why];

  return (
    <VuiBox
      sx={{
        background: "#ffffff",
        border: "1px solid #e2e8f0",
        borderRadius: "16px",
        p: "20px 24px",
        mb: "12px",
        cursor: "pointer",
        transition: "border-color .2s",
        "&:hover": { borderColor: "#cbd5e1" },
      }}
      onClick={() => setOpen((v) => !v)}
    >
      {/* ── Header row ── */}
      <VuiBox display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap="12px">
        <VuiBox display="flex" alignItems="center" gap="14px">
          <VuiBox
            component="img"
            src={service.logo}
            alt={service.name}
            sx={{ height: 28, maxWidth: 80, objectFit: "contain" }}
            onError={(e) => { e.target.style.display = "none"; }}
          />
          <VuiBox>
            <VuiTypography variant="button" fontWeight="bold" sx={{ fontSize: FS.sm, lineHeight: 1.3, color: TC.primary }}>
              {service.name}
            </VuiTypography>
            <VuiTypography variant="caption" sx={{ fontSize: FS.xs, display: "block", mt: "2px", color: TC.secondary }}>
              {service.category}
            </VuiTypography>
          </VuiBox>
        </VuiBox>
        <VuiBox display="flex" alignItems="center" gap="10px">
          <Chip
            label={sc.label}
            size="small"
            sx={{ background: sc.bg, color: sc.text, fontWeight: 700, fontSize: "11px", height: 22, border: `1px solid ${sc.text}40` }}
          />
          <VuiTypography variant="caption" sx={{ fontSize: FS.xs, color: TC.tertiary }}>
            {open ? "▲ less" : "▼ more"}
          </VuiTypography>
        </VuiBox>
      </VuiBox>

      {/* ── Summary (always visible) ── */}
      <VuiTypography variant="caption" sx={{ fontSize: FS.xs, mt: "10px", display: "block", lineHeight: 1.6, color: TC.secondary }}>
        {service.what}
      </VuiTypography>

      {/* ── Expanded detail ── */}
      <Collapse in={open}>
        <Divider sx={{ borderColor: "#e2e8f0", my: "14px" }} />

        {/* Why we use it */}
        <VuiTypography variant="caption" sx={{ fontSize: FS.xs, fontWeight: FW.bold, mb: "6px", display: "block", color: TC.primary }}>
          Why LedgerHub uses it
        </VuiTypography>
        {why.map((line, i) => (
          <VuiTypography key={i} variant="caption" sx={{ fontSize: FS.xs, display: "block", mb: "4px", lineHeight: 1.6, pl: "8px", color: TC.secondary }}>
            {why.length > 1 ? `• ${line}` : line}
          </VuiTypography>
        ))}

        {/* Without it */}
        <VuiBox sx={{ mt: "12px", p: "10px 14px", background: "rgba(244,67,54,0.06)", borderRadius: "8px", border: "1px solid rgba(244,67,54,0.2)" }}>
          <VuiTypography variant="caption" sx={{ fontSize: FS.xs, fontWeight: FW.bold, display: "block", mb: "4px", color: TC.primary }}>
            ⚠ If this stops working
          </VuiTypography>
          <VuiTypography variant="caption" sx={{ fontSize: FS.xs, lineHeight: 1.6, color: TC.secondary }}>
            {service.without}
          </VuiTypography>
        </VuiBox>

        {/* Technical details */}
        {service.details?.length > 0 && (
          <VuiBox sx={{ mt: "12px", display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {service.details.map((d) => (
              <VuiBox
                key={d.label}
                sx={{ background: "#f8fafc", borderRadius: "8px", px: "12px", py: "7px", minWidth: 160, border: "1px solid #e2e8f0" }}
              >
                <VuiTypography variant="caption" sx={{ fontSize: "10px", color: TC.tertiary, display: "block" }}>
                  {d.label}
                </VuiTypography>
                <VuiTypography variant="caption" sx={{ fontSize: FS.xs, fontWeight: FW.medium, color: TC.primary }}>
                  {d.value}
                </VuiTypography>
              </VuiBox>
            ))}
          </VuiBox>
        )}

        {/* Link */}
        {service.website && (
          <VuiBox mt="12px">
            <VuiTypography
              component="a"
              href={service.website}
              target="_blank"
              rel="noopener noreferrer"
              variant="caption"
              sx={{ fontSize: FS.xs, color: TC.link, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
              onClick={(e) => e.stopPropagation()}
            >
              Open {service.name} dashboard ↗
            </VuiTypography>
          </VuiBox>
        )}
      </Collapse>
    </VuiBox>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function IntegrationsPage() {
  const [activeCategory, setActiveCategory] = useState("All");
  const categories = ["All", ...CATEGORIES];

  const filtered = activeCategory === "All"
    ? SERVICES
    : SERVICES.filter((s) => s.category === activeCategory);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox p="24px">

        {/* Page header */}
        <VuiBox mb="6px">
          <VuiTypography variant="h4" color="white" fontWeight="bold" sx={{ fontSize: FS.xl }}>
            Integrations & Services
          </VuiTypography>
          <VuiTypography variant="caption" color="text" sx={{ fontSize: FS.xs, mt: "4px", display: "block", opacity: 0.7 }}>
            Every third-party service LedgerHub connects to — what it does, why it's needed, and what breaks if it goes down.
            Click any card to expand.
          </VuiTypography>
        </VuiBox>

        {/* Summary chips */}
        <VuiBox display="flex" gap="8px" flexWrap="wrap" mb="24px" mt="16px">
          <VuiBox sx={{ background: "rgba(76,175,80,0.12)", border: "1px solid rgba(76,175,80,0.3)", borderRadius: "8px", px: "12px", py: "6px" }}>
              <VuiTypography variant="caption" sx={{ fontSize: FS.xs, color: "#4caf50", fontWeight: FW.bold }}>
                {SERVICES.length} Active
              </VuiTypography>
            </VuiBox>
          <VuiBox sx={{ background: "rgba(158,158,158,0.1)", border: "1px solid rgba(158,158,158,0.2)", borderRadius: "8px", px: "12px", py: "6px" }}>
            <VuiTypography variant="caption" sx={{ fontSize: FS.xs, color: "#9e9e9e", fontWeight: FW.bold }}>
              {SERVICES.length} Total integrations
            </VuiTypography>
          </VuiBox>
        </VuiBox>

        {/* Category filter */}
        <VuiBox display="flex" gap="8px" flexWrap="wrap" mb="20px">
          {categories.map((cat) => (
            <VuiBox
              key={cat}
              onClick={() => setActiveCategory(cat)}
              sx={{
                px: "14px", py: "6px", borderRadius: "20px", cursor: "pointer",
                background: activeCategory === cat ? "rgba(255,255,255,0.12)" : "transparent",
                border: `1px solid ${activeCategory === cat ? "rgba(255,255,255,0.3)" : "rgba(255,255,255,0.1)"}`,
                transition: "all .2s",
                "&:hover": { background: "rgba(255,255,255,0.08)" },
              }}
            >
              <VuiTypography variant="caption" color="white" sx={{ fontSize: FS.xs, fontWeight: activeCategory === cat ? FW.bold : FW.regular }}>
                {cat}
              </VuiTypography>
            </VuiBox>
          ))}
        </VuiBox>

        {/* Service cards grouped by category */}
        {(activeCategory === "All" ? CATEGORIES : [activeCategory]).map((cat) => {
          const items = filtered.filter((s) => s.category === cat);
          if (!items.length) return null;
          return (
            <VuiBox key={cat} mb="28px">
              <VuiTypography variant="overline" color="text" sx={{ fontSize: "11px", opacity: 0.5, letterSpacing: "0.1em", mb: "10px", display: "block" }}>
                {cat}
              </VuiTypography>
              {items.map((service) => (
                <ServiceCard key={service.name} service={service} />
              ))}
            </VuiBox>
          );
        })}

      </VuiBox>
    </DashboardLayout>
  );
}

